import { useState, useRef, useEffect } from 'react'
import { Globe } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from './button'
import { parseLangFromPath } from '@/utils/parseMenuSlug'

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
  { code: 'es', label: 'ES' },
] as const

export function LanguageSwitcher() {
  const { i18n } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) ?? LANGUAGES[0]

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen])

  const handleSelect = (code: string) => {
    // Build new URL with the selected language prefix
    const { pathWithoutLang } = parseLangFromPath(window.location.pathname)
    const newPath = code === 'en' ? pathWithoutLang : `/${code}${pathWithoutLang}`

    // Update URL and trigger route change in App.tsx
    window.history.pushState({}, '', newPath)
    window.dispatchEvent(new PopStateEvent('popstate'))

    setIsOpen(false)
  }

  return (
    <div ref={containerRef} className="relative">
      <Button
        variant="ghost"
        size="icon"
        className="shrink-0 rounded-full h-9 w-9"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label="Change language"
      >
        <Globe className="h-4 w-4" />
      </Button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 z-50 min-w-[80px] rounded-lg border border-border bg-background shadow-lg py-1 animate-fade-in">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => handleSelect(lang.code)}
              className={`w-full text-left px-3 py-2 text-sm font-medium transition-colors hover:bg-muted ${
                lang.code === currentLang.code
                  ? 'text-primary bg-primary/5'
                  : 'text-foreground'
              }`}
            >
              {lang.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
